import { useCallback, useEffect, useState } from "react";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import { Eye, MousePointerClick, Users, Smartphone } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { Card, PageTitle, Btn } from "./ui";
import { DateRange } from "../components/site/DateRange";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-[#D4AF37]/20 bg-[#101117] p-5" data-testid={testid}>
    <div className="flex items-center justify-between text-zinc-500"><span className="text-xs uppercase tracking-widest">{label}</span><Icon size={16} className="text-[#D4AF37]" /></div>
    <p className="mt-3 font-serif text-3xl font-bold text-white">{value}</p>
  </div>
);

const TopList = ({ items, testid }) => {
  const max = Math.max(1, ...items.map((i) => i.count));
  if (!items.length) return <p className="text-sm text-zinc-600">No data yet.</p>;
  return (
    <ul className="space-y-3" data-testid={testid}>
      {items.map((i) => (
        <li key={i.name}>
          <div className="flex justify-between gap-3 text-sm"><span className="min-w-0 truncate text-zinc-300" title={i.name}>{i.name}</span><span className="shrink-0 text-[#D4AF37]">{i.count}</span></div>
          <div className="mt-1 h-1 rounded-full bg-white/5"><div className="h-1 rounded-full bg-[#D4AF37]" style={{ width: `${(i.count / max) * 100}%` }} /></div>
        </li>
      ))}
    </ul>
  );
};

export default function Dashboard() {
  const [days, setDays] = useState(14);
  const [range, setRange] = useState({ start: "", end: "" });
  const [d, setD] = useState(null);
  const custom = range.start && range.end;
  const load = useCallback((isLive = () => true) => {
    const q = custom ? `start=${range.start}&end=${range.end}` : `days=${days}`;
    return api.get(`/admin/analytics?${q}`).then(({ data }) => isLive() && setD(data)).catch((e) => toast.error(errMsg(e)));
  }, [days, range, custom]);
  useEffect(() => {
    let live = true;
    load(() => live);
    return () => { live = false; };
  }, [load]);
  const label = custom ? "range" : `${days}d`;

  const block = async (ip) => {
    try {
      await api.post("/admin/blocked", { ip, reason: "Blocked from dashboard" });
      toast.success(`${ip} blocked`);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!d) return <p className="text-zinc-500">Loading analytics…</p>;
  const mobile = d.devices.find((x) => x.name === "mobile")?.count || 0;
  const totalDev = d.devices.reduce((a, b) => a + b.count, 0) || 1;
  return (
    <div data-testid="admin-dashboard">
      <PageTitle title="Dashboard" sub="Daily visitors, link clicks and traffic sources">
        {[7, 14, 30].map((n) => <Btn key={n} variant={!custom && days === n ? "gold" : "outline"} onClick={() => { setRange({ start: "", end: "" }); setDays(n); }} data-testid={`range-${n}`}>{n} days</Btn>)}
      </PageTitle>
      <div className="mb-6 flex flex-wrap items-center gap-3">
        <span className="text-xs uppercase tracking-widest text-zinc-500">Custom range</span>
        <DateRange start={range.start} end={range.end} onChange={setRange} testid="dash-date" />
        {custom && <span className="text-xs text-[#D4AF37]" data-testid="dash-range-active">Showing {d.daily.length} days</span>}
      </div>
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Users} label={`Unique visitors (${label})`} value={d.unique_visitors} testid="stat-visitors-today" />
        <Stat icon={Eye} label={`Page views (${label})`} value={d.totals.pageviews} testid="stat-pageviews" />
        <Stat icon={MousePointerClick} label={`Link clicks (${label})`} value={d.totals.clicks} testid="stat-clicks" />
        <Stat icon={Smartphone} label="Mobile share" value={`${Math.round((mobile / totalDev) * 100)}%`} testid="stat-mobile" />
      </div>
      <Card title="Daily traffic" className="mt-6" testid="daily-chart">
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.daily} margin={{ left: -20, right: 8 }}>
              <defs><linearGradient id="g" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#D4AF37" stopOpacity={0.5} /><stop offset="100%" stopColor="#D4AF37" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="#ffffff10" vertical={false} />
              <XAxis dataKey="day" tickFormatter={(v) => v.slice(5)} stroke="#71717a" fontSize={11} />
              <YAxis stroke="#71717a" fontSize={11} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#101117", border: "1px solid #D4AF3740", borderRadius: 8 }} />
              <Area type="monotone" dataKey="visitors" stroke="#D4AF37" fill="url(#g)" strokeWidth={2} />
              <Area type="monotone" dataKey="pageviews" stroke="#F3D77A" fill="none" strokeDasharray="4 4" />
              <Area type="monotone" dataKey="clicks" stroke="#25D366" fill="none" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <p className="mt-2 text-xs text-zinc-500"><span className="text-[#D4AF37]">— visitors</span> · <span className="text-[#F3D77A]">- - page views</span> · <span className="text-[#25D366]">— clicks</span></p>
      </Card>
      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3 [&>*]:min-w-0">
        <Card title="Top clicked links"><TopList items={d.top_clicks} testid="top-clicks" /></Card>
        <Card title="Top pages"><TopList items={d.top_pages} testid="top-pages" /></Card>
        <Card title="Traffic sources"><TopList items={d.referrers.map((r) => ({ ...r, name: r.name === "(none)" || !r.name ? "Direct" : r.name }))} testid="top-referrers" /></Card>
      </div>
      <Card title="Recent visitors" className="mt-6" testid="recent-visitors">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead className="text-xs uppercase tracking-wider text-zinc-500 [&_th]:pb-3 [&_th]:align-bottom"><tr><th>IP</th><th>Hits</th><th>Device</th><th>Last page</th><th>Last seen</th><th /></tr></thead>
            <tbody className="divide-y divide-white/5">
              {d.visitors.map((v) => (
                <tr key={v.ip} data-testid={`visitor-row-${v.ip}`}>
                  <td className="py-3 font-mono text-xs text-zinc-300">{v.ip} {v.suspicious && <span className="ml-1 rounded bg-red-500/15 px-1.5 py-0.5 text-[10px] text-red-400">suspicious</span>}</td>
                  <td>{v.hits}</td><td className="capitalize text-zinc-400">{v.device}</td>
                  <td className="max-w-[160px] truncate text-zinc-400">{v.last_path}</td>
                  <td className="text-xs text-zinc-500">{new Date(v.last_seen).toLocaleString()}</td>
                  <td className="text-right">{v.blocked ? <span className="text-xs text-red-400">Blocked</span> : <Btn variant="danger" onClick={() => block(v.ip)} data-testid={`block-${v.ip}`}>Block</Btn>}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!d.visitors.length && <p className="py-4 text-sm text-zinc-600">No visitors yet.</p>}
        </div>
      </Card>
    </div>
  );
}
